import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    ReceiptLong as LedgerIcon,
} from '@mui/icons-material';
import { toJalaali } from 'jalaali-js';
import useFetch from '../../../../../hooks/useFetch';
import useDeleteItem from '../../../../../hooks/useDeleteItem';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import SearchableAutocomplete from '../../../../SearchableAutocomplete';
import TeacherSettlementForm from './TeacherSettlementForm';
import TeacherLedger from './TeacherLedger';
import api from '../../../../../config/axiosConfig';

const PERSIAN_MONTHS = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];

// Current Jalali month "YYYY-MM" — months before this are "completed" (payable);
// the in-progress month is not yet due.
const CURRENT_YM = (() => {
    const n = new Date();
    const { jy, jm } = toJalaali(n.getFullYear(), n.getMonth() + 1, n.getDate());
    return `${jy}-${String(jm).padStart(2, '0')}`;
})();

const monthLabelFromYM = (ym) => {
    if (!ym) return '';
    const [y, m] = String(ym).split('-').map(Number);
    return `${PERSIAN_MONTHS[(m || 1) - 1] || ''} ${toPersianDigits(y || '')}`.trim();
};

function TeacherSettlements() {
    const [personType, setPersonType] = useState('teacher'); // 'teacher' | 'staff'
    const [summaryFilters, setSummaryFilters] = useState({
        teacher_id: '',
        status: 'active',
    });
    const isStaff = personType === 'staff';
    const personLabel = isStaff ? 'پرسنل' : 'آموزگار';
    const [summaryTeacherOption, setSummaryTeacherOption] = useState(null);
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [formDefaults, setFormDefaults] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [selectedSettlement, setSelectedSettlement] = useState(null);
    const [settlementToDelete, setSettlementToDelete] = useState(null);
    const [settlementToApprove, setSettlementToApprove] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [refreshToken, setRefreshToken] = useState(0);
    const [ledgerTeacher, setLedgerTeacher] = useState(null);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    const summaryParams = useMemo(() => {
        const params = { person: personType };
        if (summaryFilters.teacher_id) {
            params[isStaff ? 'staff_id' : 'teacher_id'] = summaryFilters.teacher_id;
        }
        // Always send status explicitly so the request distinctly encodes the
        // filter (otherwise 'active' is indistinguishable from no filter).
        params.status = summaryFilters.status || 'active';
        return params;
    }, [summaryFilters, personType, isStaff]);

    const { data: summaryData, loading: summaryLoading, error: summaryError, refetch: refetchSummary } = useFetch(API.teacherSettlementSummaryMonthly, {
        params: summaryParams,
    });

    const summaries = useMemo(() => {
        const rows = Array.isArray(summaryData) ? summaryData : [];
        return [...rows].sort((a, b) =>
            (a.teacher_name || '').localeCompare(b.teacher_name || '', 'fa')
        );
    }, [summaryData]);

    const hasActiveSummaryFilters = Object.entries(summaryFilters).some(
        ([key, v]) => key === 'status' ? v !== 'active' : v !== ''
    );

    // What's owed to a teacher right now: last completed month + earlier unpaid
    // months. The in-progress month is excluded (not yet due).
    const getDue = (summary) => {
        const completed = (summary.months || []).filter((m) => m.year_month < CURRENT_YM);
        const last = completed.length ? completed[completed.length - 1] : null;
        const previousDue = completed.slice(0, -1).reduce((s, m) => s + (m.remaining_balance || 0), 0);
        return {
            lastLabel: last ? monthLabelFromYM(last.year_month) : null,
            lastRemaining: last ? (last.remaining_balance || 0) : 0,
            previousDue,
        };
    };

    const handleSummaryFilterChange = (field, value, option = null) => {
        setSummaryFilters(prev => ({ ...prev, [field]: value }));
        if (field === 'teacher_id') {
            setSummaryTeacherOption(option);
        }
    };

    const handleClearSummaryFilters = () => {
        setSummaryFilters({ teacher_id: '', status: 'active' });
        setSummaryTeacherOption(null);
    };

    const handlePersonTypeChange = (newType) => {
        if (newType === personType) return;
        setPersonType(newType);
        setSummaryFilters({ teacher_id: '', status: 'active' });
        setSummaryTeacherOption(null);
    };

    // Add a payment from inside a person's ledger: person + month pre-filled.
    const handleAddPaymentForMonth = (month) => {
        setSelectedSettlement(null);
        setFormDefaults(ledgerTeacher
            ? {
                person_type: personType,
                teacher_id: ledgerTeacher.teacher_id,
                teacher_name: ledgerTeacher.teacher_name,
                settlement_period: month,
            }
            : null);
        setFormDialogOpen(true);
    };

    const handleEdit = (settlement) => {
        setSelectedSettlement(settlement);
        setFormDefaults(null);
        setFormDialogOpen(true);
    };

    const handleDelete = (settlement) => {
        setSettlementToDelete(settlement);
        setDeleteDialogOpen(true);
    };

    const handleApprove = (settlement) => {
        setSettlementToApprove(settlement);
        setApproveDialogOpen(true);
    };

    const closeForm = () => {
        setFormDialogOpen(false);
        setSelectedSettlement(null);
        setFormDefaults(null);
    };

    const confirmDelete = async () => {
        if (!settlementToDelete) return;
        try {
            await deleteItem(`${API.teacherSettlements}${settlementToDelete.id}/`);
            setSnackbar({ open: true, message: 'تسویه با موفقیت حذف شد.', severity: 'success' });
            refetchSummary();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.detail || 'خطا در حذف تسویه', severity: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setSettlementToDelete(null);
        }
    };

    const confirmApprove = async () => {
        if (!settlementToApprove) return;
        try {
            await api.post(`${API.teacherSettlements}${settlementToApprove.id}/approve/`);
            setSnackbar({ open: true, message: 'تسویه با موفقیت تأیید شد.', severity: 'success' });
            refetchSummary();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.detail || 'خطا در تأیید تسویه', severity: 'error' });
        } finally {
            setApproveDialogOpen(false);
            setSettlementToApprove(null);
        }
    };

    const handleFormSuccess = () => {
        const wasEdit = !!selectedSettlement;
        closeForm();
        refetchSummary();
        setRefreshToken(prev => prev + 1);
        setSnackbar({
            open: true,
            message: wasEdit ? 'تسویه با موفقیت ویرایش شد.' : 'تسویه با موفقیت ثبت شد.',
            severity: 'success',
        });
    };

    const formatCurrency = (amount) => toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return toPersianDigits(new Date(dateString).toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    return (
        <RTL>
            {ledgerTeacher ? (
                <TeacherLedger
                    teacher={ledgerTeacher}
                    personType={personType}
                    onBack={() => setLedgerTeacher(null)}
                    onAddPayment={handleAddPaymentForMonth}
                    onEditPayment={handleEdit}
                    onDeletePayment={handleDelete}
                    onApprovePayment={handleApprove}
                    refreshToken={refreshToken}
                />
            ) : (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                        تسویه حقوق
                    </Typography>

                    <Paper sx={{ mb: 3 }}>
                        <Tabs
                            value={personType}
                            onChange={(e, v) => handlePersonTypeChange(v)}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab value="teacher" label="آموزگاران" />
                            <Tab value="staff" label="پرسنل" />
                        </Tabs>
                    </Paper>

                    {/* Filters */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <FilterListIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">فیلترها</Typography>
                            {hasActiveSummaryFilters && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={handleClearSummaryFilters} sx={{ mr: 'auto' }}>
                                    پاک کردن فیلترها
                                </Button>
                            )}
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid
                            container
                            spacing={0}
                            sx={{ '& .MuiTextField-root': { m: 0 }, '& .MuiFormControl-root': { m: 0 } }}
                        >
                            <Grid item xs={12} sm={6} sx={{ px: 2, pb: 2 }}>
                                <SearchableAutocomplete
                                    key={personType}
                                    apiUrl={isStaff ? '/accounts/api/v1/profile/staffs/' : '/accounts/api/v1/profile/teachers/'}
                                    getOptionLabel={(option) =>
                                        `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.email || ''
                                    }
                                    getOptionValue={(option) => option?.user?.id}
                                    value={summaryFilters.teacher_id || null}
                                    onChange={(value, option) => handleSummaryFilterChange('teacher_id', value || '', option)}
                                    initialOption={summaryTeacherOption}
                                    label={personLabel}
                                    size="small"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={{ px: 2, pb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{`وضعیت ${personLabel}`}</InputLabel>
                                    <Select
                                        value={summaryFilters.status}
                                        label={`وضعیت ${personLabel}`}
                                        onChange={(e) => handleSummaryFilterChange('status', e.target.value)}
                                    >
                                        <MenuItem value="active">فعال</MenuItem>
                                        <MenuItem value="inactive">آرشیوشده</MenuItem>
                                        <MenuItem value="all">همه</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {summaryLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!summaryLoading && summaryError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{summaryError}</Alert>
                    )}

                    {!summaryLoading && !summaryError && summaries.length === 0 && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">{`${personLabel}ی یافت نشد.`}</Typography>
                        </Paper>
                    )}

                    {/* One compact row per teacher: last month due + earlier unpaid. */}
                    {!summaryLoading && !summaryError && summaries.map((summary) => {
                        const due = getDue(summary);
                        return (
                            <Paper
                                key={summary.teacher_id}
                                variant="outlined"
                                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, px: 2, py: 1, mb: 0.5 }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                                    {summary.teacher_name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3, ml: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {due.lastLabel ? `${due.lastLabel}:` : 'ماه گذشته:'}{' '}
                                        <Box component="span" sx={{ fontWeight: 'bold', color: due.lastRemaining > 0 ? 'warning.main' : 'success.main' }}>
                                            {formatCurrency(due.lastRemaining)} تومان
                                        </Box>
                                    </Typography>
                                    {due.previousDue > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                            معوقات قبلی:{' '}
                                            <Box component="span" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                                {formatCurrency(due.previousDue)} تومان
                                            </Box>
                                        </Typography>
                                    )}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<LedgerIcon />}
                                        onClick={() => setLedgerTeacher(summary)}
                                    >
                                        مشاهده دفتر
                                    </Button>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            {/* Create/Edit Form Dialog (shared by the overview and the ledger) */}
            <Dialog open={formDialogOpen} onClose={closeForm} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedSettlement ? 'ویرایش تسویه' : 'ثبت تسویه جدید'}
                </DialogTitle>
                <DialogContent>
                    <TeacherSettlementForm
                        settlement={selectedSettlement}
                        defaults={formDefaults}
                        onSuccess={handleFormSuccess}
                        onCancel={closeForm}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setSettlementToDelete(null); }}>
                <DialogTitle>تأیید حذف</DialogTitle>
                <DialogContent>
                    <Typography>
                        آیا از حذف این تسویه اطمینان دارید؟ این عمل قابل بازگشت نیست.
                    </Typography>
                    {settlementToDelete && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">آموزگار: {settlementToDelete.teacher_name}</Typography>
                            <Typography variant="body2" color="text.secondary">مبلغ: {formatCurrency(settlementToDelete.amount)} تومان</Typography>
                            <Typography variant="body2" color="text.secondary">تاریخ: {formatDate(settlementToDelete.payment_date)}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDeleteDialogOpen(false); setSettlementToDelete(null); }}>انصراف</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" disabled={isDeleting}>حذف</Button>
                </DialogActions>
            </Dialog>

            {/* Approve Confirmation Dialog */}
            <Dialog open={approveDialogOpen} onClose={() => { setApproveDialogOpen(false); setSettlementToApprove(null); }}>
                <DialogTitle>تأیید تسویه</DialogTitle>
                <DialogContent>
                    <Typography>آیا از تأیید این تسویه اطمینان دارید؟</Typography>
                    {settlementToApprove && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">آموزگار: {settlementToApprove.teacher_name}</Typography>
                            <Typography variant="body2" color="text.secondary">مبلغ: {formatCurrency(settlementToApprove.amount)} تومان</Typography>
                            <Typography variant="body2" color="text.secondary">تاریخ: {formatDate(settlementToApprove.payment_date)}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setApproveDialogOpen(false); setSettlementToApprove(null); }}>انصراف</Button>
                    <Button onClick={confirmApprove} color="success" variant="contained">تأیید</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </RTL>
    );
}

export default TeacherSettlements;
